import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import useToast from '../components/useToast';
import { saisonNameZuId, geschaetzteAktuelleSaisonId, saisonIdZuName } from '../utils/saison';
import AdminDashboard from './admin/AdminDashboard';
import SaisonSetup from './admin/SaisonSetup';
import Punktesystem from './admin/Punktesystem';
import BonusKonfiguration from './admin/BonusKonfiguration';
import BonusAuswertung from './admin/BonusAuswertung';
import HallOfFameVerwaltung from './admin/HallOfFameVerwaltung';
import './admin/admin.css';

// Admin-Bereich: Saison wählen, Reiter, gemeinsame Konfiguration laden.
// Die Teilseiten unter ./admin/ schreiben selbst und melden per neuLaden(),
// dass die Konfiguration neu gelesen werden soll.

const TABS = [
  { key: 'dashboard', label: '🏠 Dashboard' },
  { key: 'saison', label: '⚙️ Saison' },
  { key: 'punkte', label: '🎯 Punktesystem' },
  { key: 'bonus', label: '🎁 Bonusfragen' },
  { key: 'auswertung', label: '🧮 Auswertung' },
  { key: 'hof', label: '🏛️ Hall of Fame' }
];

const saisonListe = () => {
  const jahr = new Date().getFullYear();
  const liste = [];
  for (let y = jahr + 2; y >= jahr - 5; y--) liste.push(`${y}/${String((y + 1) % 100).padStart(2, '0')}`);
  return liste;
};

function AdminPanel() {
  const { zeigeToast, ToastElement } = useToast();
  const [saisonName, setSaisonName] = useState(() => {
    try {
      return localStorage.getItem('adminAusgewaehlteSaison') || saisonIdZuName(geschaetzteAktuelleSaisonId());
    } catch (e) {
      return saisonIdZuName(geschaetzteAktuelleSaisonId());
    }
  });
  const [tab, setTab] = useState('dashboard');
  const [config, setConfig] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const saisonId = saisonNameZuId(saisonName);

  const neuLaden = useCallback(async () => {
    const snap = await getDoc(doc(db, 'saisonKonfiguration', saisonId));
    setConfig(snap.exists() ? snap.data() : {});
  }, [saisonId]);

  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    neuLaden().catch(e => console.error('Konfiguration konnte nicht geladen werden:', e))
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [neuLaden]);

  const wechsleSaison = (name) => {
    setSaisonName(name);
    try { localStorage.setItem('adminAusgewaehlteSaison', name); } catch (e) { /* egal */ }
  };

  const props = { saisonId, saisonName, config, neuLaden, zeigeToast, wechsleTab: setTab };

  return (
    <div className="seite admin-seite">
      <div className="admin-kopf">
        <h1>🛠️ Admin</h1>
        <label className="admin-kopf__saison">
          Saison
          <select value={saisonName} onChange={e => wechsleSaison(e.target.value)}>
            {saisonListe().map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="chips admin-tabs" role="tablist">
        {TABS.map(t => (
          <button key={t.key} type="button" role="tab" className={`chip${tab === t.key ? ' ist-aktiv' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {laedt || config === null ? (
        <div className="karte karte--leer">⏳ Lade Konfiguration…</div>
      ) : (
        <>
          {tab === 'dashboard' && <AdminDashboard {...props} />}
          {tab === 'saison' && <SaisonSetup {...props} />}
          {tab === 'punkte' && <Punktesystem {...props} />}
          {tab === 'bonus' && <BonusKonfiguration {...props} />}
          {tab === 'auswertung' && (
            <BonusAuswertung saisonId={saisonId} config={config?.bonusfragen || null} gespeicherteLoesung={config?.bonusfragenLoesung || null} />
          )}
          {tab === 'hof' && <HallOfFameVerwaltung {...props} />}
        </>
      )}

      {ToastElement}
    </div>
  );
}

export default AdminPanel;
